import { AdminBreadcrumb } from "@/components/admin/AdminBreadcrumb";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
	useAdminCourse,
	useCreateModule,
	useDeleteQuiz,
	useUpdateModule,
	useUpsertQuiz,
} from "@/hooks/useAdminData";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronDown, Plus, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

const optionSchema = z.object({
	text: z.string().min(1, "Option text is required"),
	is_correct: z.boolean(),
});

const questionSchema = z.object({
	type: z.enum(["single_choice", "multiple_choice"]),
	text: z.string().min(1, "Question text is required"),
	options: z.array(optionSchema).min(2, "At least 2 options"),
	explanation: z.string(),
});

const moduleSchema = z.object({
	title: z.string().min(1, "Title is required"),
	description: z.string(),
	video_id: z.string(),
	duration_sec: z.coerce.number().min(0),
});

const quizSchema = z.object({
	passing_score: z.coerce.number().min(0).max(100),
	questions: z.array(questionSchema),
});

type ModuleFormValues = z.infer<typeof moduleSchema>;
type QuizFormValues = z.infer<typeof quizSchema>;

export function AdminModuleEditor() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { id: courseId, mid: moduleId } = useParams<{
		id: string;
		mid: string;
	}>();
	const isNew = !moduleId || moduleId === "new";

	const { data: course, isLoading } = useAdminCourse(courseId ?? "");
	const allModules = course?.sections?.flatMap((s) => s.modules) ?? [];
	const existingModule = allModules.find((m) => m.id === moduleId);

	// For new modules, pick sectionId from query param or let the user choose
	const searchParams = new URLSearchParams(window.location.search);
	const sectionIdParam = searchParams.get("section") ?? "";
	const existingSectionId =
		course?.sections?.find((s) => s.modules.some((m) => m.id === moduleId))?.id ?? "";
	const [selectedSectionId, setSelectedSectionId] = useState(sectionIdParam);
	const sectionId = isNew ? selectedSectionId || sectionIdParam : existingSectionId;

	const createMutation = useCreateModule(courseId ?? "", sectionId);
	const updateMutation = useUpdateModule(courseId ?? "", moduleId ?? "");
	const quizMutation = useUpsertQuiz(moduleId ?? "");
	const deleteQuizMutation = useDeleteQuiz(moduleId ?? "");

	const [quizOpen, setQuizOpen] = useState(false);

	const {
		register: registerModule,
		handleSubmit: handleModuleSubmit,
		reset: resetModule,
		formState: { errors: moduleErrors },
	} = useForm<ModuleFormValues>({
		resolver: zodResolver(moduleSchema),
		defaultValues: {
			title: "",
			description: "",
			video_id: "",
			duration_sec: 0,
		},
	});

	const {
		register: registerQuiz,
		handleSubmit: handleQuizSubmit,
		control: quizControl,
		reset: resetQuiz,
		formState: { errors: quizErrors },
	} = useForm<QuizFormValues>({
		resolver: zodResolver(quizSchema),
		defaultValues: {
			passing_score: 70,
			questions: [],
		},
	});

	const {
		fields: questionFields,
		append: appendQuestion,
		remove: removeQuestion,
	} = useFieldArray({
		control: quizControl,
		name: "questions",
	});

	useEffect(() => {
		if (existingModule) {
			resetModule({
				title: existingModule.title,
				description: existingModule.description,
				video_id: "",
				duration_sec: existingModule.duration_sec,
			});
			if (existingModule.has_quiz) {
				setQuizOpen(true);
			}
		}
	}, [existingModule, resetModule]);

	async function onModuleSubmit(values: ModuleFormValues) {
		if (isNew) {
			const sectionModules = course?.sections?.find((s) => s.id === sectionId)?.modules ?? [];
			const nextPosition = sectionModules.length + 1;
			createMutation.mutate(
				{
					title: values.title,
					description: values.description,
					position: nextPosition,
					duration_sec: values.duration_sec,
					...(values.video_id ? { video_id: values.video_id } : {}),
				},
				{
					onSuccess: (created) => {
						toast.success(t("admin.moduleCreated"));
						navigate(`/admin/courses/${courseId}/modules/${created.id}`);
					},
					onError: () => toast.error(t("admin.saveFailed")),
				},
			);
		} else {
			updateMutation.mutate(
				{
					title: values.title,
					description: values.description,
					duration_sec: values.duration_sec,
					...(values.video_id ? { video_id: values.video_id } : {}),
				},
				{
					onSuccess: () => toast.success(t("admin.moduleSaved")),
					onError: () => toast.error(t("admin.saveFailed")),
				},
			);
		}
	}

	async function onQuizSubmit(values: QuizFormValues) {
		const body = {
			passing_score: values.passing_score,
			questions: values.questions.map((q, qi) => ({
				id: `q-${qi}`,
				type: q.type,
				text: q.text,
				options: q.options.map((o, oi) => ({
					id: `q-${qi}-o-${oi}`,
					text: o.text,
					is_correct: o.is_correct,
				})),
				explanation: q.explanation,
			})),
		};
		quizMutation.mutate(body as Parameters<typeof quizMutation.mutate>[0], {
			onSuccess: () => toast.success(t("admin.quizSaved")),
			onError: () => toast.error(t("admin.saveFailed")),
		});
	}

	function handleDeleteQuiz() {
		deleteQuizMutation.mutate(undefined, {
			onSuccess: () => {
				toast.success(t("admin.quizDeleted"));
				resetQuiz({ passing_score: 70, questions: [] });
				setQuizOpen(false);
			},
		});
	}

	if (!isNew && isLoading) {
		return (
			<div className="space-y-6">
				<Skeleton className="h-6 w-64" />
				<Skeleton className="h-8 w-48" />
				<div className="space-y-4">
					{Array.from({ length: 4 }).map((_, i) => (
						<Skeleton key={`field-${i}`} className="h-10 w-full" />
					))}
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<AdminBreadcrumb
				items={[
					{ label: t("admin.dashboard"), href: "/admin" },
					{ label: t("admin.courses"), href: "/admin/courses" },
					{
						label: course?.title ?? "...",
						href: `/admin/courses/${courseId}`,
					},
					{
						label: isNew ? t("admin.newModule") : (existingModule?.title ?? t("admin.editModule")),
					},
				]}
			/>

			<h1 className="text-3xl font-bold text-primary">
				{isNew ? t("admin.newModule") : t("admin.editModule")}
			</h1>

			{/* Module form */}
			<form onSubmit={handleModuleSubmit(onModuleSubmit)} className="max-w-2xl space-y-6">
				{/* Section selector — for new modules without a pre-selected section */}
				{isNew && !sectionIdParam && course?.sections && course.sections.length > 0 && (
					<div className="space-y-2">
						<Label>{t("admin.currentSection")}</Label>
						<Select value={selectedSectionId} onValueChange={setSelectedSectionId}>
							<SelectTrigger>
								<SelectValue placeholder={t("admin.selectCoursePlaceholder")} />
							</SelectTrigger>
							<SelectContent>
								{course.sections
									.sort((a, b) => a.position - b.position)
									.map((s) => (
										<SelectItem key={s.id} value={s.id}>
											{s.title}
										</SelectItem>
									))}
							</SelectContent>
						</Select>
					</div>
				)}

				{/* Current section info — when editing, show which section this module belongs to */}
				{!isNew && existingSectionId && course?.sections && (
					<div className="space-y-2">
						<Label>{t("admin.currentSection")}</Label>
						<Select
							value={existingSectionId}
							onValueChange={(newSectionId) => {
								if (newSectionId !== existingSectionId) {
									updateMutation.mutate(
										{ section_id: newSectionId },
										{
											onSuccess: () => toast.success(t("admin.moduleSaved")),
											onError: () => toast.error(t("admin.saveFailed")),
										},
									);
								}
							}}
						>
							<SelectTrigger>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{course.sections
									.sort((a, b) => a.position - b.position)
									.map((s) => (
										<SelectItem key={s.id} value={s.id}>
											{s.title}
										</SelectItem>
									))}
							</SelectContent>
						</Select>
					</div>
				)}

				<div className="space-y-2">
					<Label htmlFor="title">{t("admin.title")}</Label>
					<Input
						id="title"
						{...registerModule("title")}
						placeholder={t("admin.moduleTitlePlaceholder")}
					/>
					{moduleErrors.title && <p className="text-sm text-error">{moduleErrors.title.message}</p>}
				</div>

				<div className="space-y-2">
					<Label htmlFor="description">{t("admin.description")}</Label>
					<Textarea
						id="description"
						{...registerModule("description")}
						placeholder={t("admin.descriptionPlaceholder")}
						rows={4}
					/>
				</div>

				<div className="grid grid-cols-2 gap-4">
					<div className="space-y-2">
						<Label htmlFor="video_id">{t("admin.videoId")}</Label>
						<Input id="video_id" {...registerModule("video_id")} placeholder="Bunny Stream GUID" />
					</div>

					<div className="space-y-2">
						<Label htmlFor="duration_sec">
							{t("admin.duration")} ({t("admin.seconds")})
						</Label>
						<Input id="duration_sec" type="number" {...registerModule("duration_sec")} min={0} />
					</div>
				</div>

				<div className="flex gap-3">
					<Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
						{isNew ? t("admin.createModule") : t("admin.save")}
					</Button>
					<Button
						type="button"
						variant="outline"
						onClick={() => navigate(`/admin/courses/${courseId}`)}
					>
						{t("admin.cancel")}
					</Button>
				</div>
			</form>

			{/* Quiz section — only when editing existing module */}
			{!isNew && (
				<div className="max-w-2xl">
					<button
						type="button"
						onClick={() => setQuizOpen(!quizOpen)}
						className="flex w-full items-center justify-between rounded-xl border border-white/10 bg-bg-elevated p-4 text-left transition-colors hover:bg-bg-overlay"
					>
						<h2 className="text-lg font-semibold text-primary">{t("admin.quiz")}</h2>
						<ChevronDown
							className={cn(
								"h-5 w-5 text-secondary transition-transform",
								quizOpen && "rotate-180",
							)}
						/>
					</button>

					{quizOpen && (
						<form
							onSubmit={handleQuizSubmit(onQuizSubmit)}
							className="mt-2 space-y-6 rounded-b-xl border border-t-0 border-white/10 bg-bg-elevated p-6"
						>
							<div className="space-y-2">
								<Label htmlFor="passing_score">{t("admin.passingScore")} (%)</Label>
								<Input
									id="passing_score"
									type="number"
									{...registerQuiz("passing_score")}
									min={0}
									max={100}
								/>
								{quizErrors.passing_score && (
									<p className="text-sm text-error">{quizErrors.passing_score.message}</p>
								)}
							</div>

							<div className="space-y-4">
								<div className="flex items-center justify-between">
									<h3 className="font-medium text-primary">{t("admin.questions")}</h3>
									<Button
										type="button"
										variant="outline"
										size="sm"
										onClick={() =>
											appendQuestion({
												type: "single_choice",
												text: "",
												options: [
													{ text: "", is_correct: false },
													{ text: "", is_correct: false },
												],
												explanation: "",
											})
										}
									>
										<Plus className="mr-1 h-4 w-4" />
										{t("admin.addQuestion")}
									</Button>
								</div>

								{questionFields.length === 0 && (
									<p className="py-4 text-center text-sm text-secondary">
										{t("admin.noQuestions")}
									</p>
								)}

								{questionFields.map((field, qIndex) => (
									<QuestionCard
										key={field.id}
										index={qIndex}
										register={registerQuiz}
										control={quizControl}
										errors={quizErrors}
										onRemove={() => removeQuestion(qIndex)}
									/>
								))}
							</div>

							<div className="flex gap-3">
								<Button type="submit" disabled={quizMutation.isPending}>
									{t("admin.saveQuiz")}
								</Button>
								{existingModule?.has_quiz && (
									<Button
										type="button"
										variant="destructive"
										onClick={handleDeleteQuiz}
										disabled={deleteQuizMutation.isPending}
									>
										{t("admin.deleteQuiz")}
									</Button>
								)}
							</div>
						</form>
					)}
				</div>
			)}
		</div>
	);
}

function QuestionCard({
	index,
	register,
	control,
	errors,
	onRemove,
}: {
	index: number;
	register: ReturnType<typeof useForm<QuizFormValues>>["register"];
	control: ReturnType<typeof useForm<QuizFormValues>>["control"];
	errors: ReturnType<typeof useForm<QuizFormValues>>["formState"]["errors"];
	onRemove: () => void;
}) {
	const { t } = useTranslation();
	const {
		fields: optionFields,
		append: appendOption,
		remove: removeOption,
	} = useFieldArray({
		control,
		name: `questions.${index}.options`,
	});

	return (
		<div className="space-y-4 rounded-lg border border-white/10 bg-bg-surface p-4">
			<div className="flex items-start justify-between">
				<span className="text-sm font-medium text-secondary">
					{t("admin.questionNumber", { number: index + 1 })}
				</span>
				<Button
					type="button"
					variant="ghost"
					size="icon"
					className="text-error hover:text-error"
					onClick={onRemove}
				>
					<Trash2 className="h-4 w-4" />
				</Button>
			</div>

			<div className="space-y-2">
				<Label>{t("admin.questionType")}</Label>
				<Controller
					control={control}
					name={`questions.${index}.type`}
					render={({ field }) => (
						<Select value={field.value} onValueChange={field.onChange}>
							<SelectTrigger>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="single_choice">{t("admin.singleChoice")}</SelectItem>
								<SelectItem value="multiple_choice">{t("admin.multipleChoice")}</SelectItem>
							</SelectContent>
						</Select>
					)}
				/>
			</div>

			<div className="space-y-2">
				<Label>{t("admin.questionText")}</Label>
				<Textarea {...register(`questions.${index}.text`)} rows={2} />
				{errors.questions?.[index]?.text && (
					<p className="text-sm text-error">{errors.questions[index].text?.message}</p>
				)}
			</div>

			<div className="space-y-3">
				<div className="flex items-center justify-between">
					<Label>{t("admin.options")}</Label>
					<Button
						type="button"
						variant="ghost"
						size="sm"
						onClick={() => appendOption({ text: "", is_correct: false })}
					>
						<Plus className="mr-1 h-3 w-3" />
						{t("admin.addOption")}
					</Button>
				</div>

				{optionFields.map((optField, oIndex) => (
					<div key={optField.id} className="flex items-center gap-2">
						<input
							type="checkbox"
							{...register(`questions.${index}.options.${oIndex}.is_correct`)}
							className="h-4 w-4 rounded border-white/20 bg-bg-input text-accent accent-accent"
						/>
						<Input
							{...register(`questions.${index}.options.${oIndex}.text`)}
							placeholder={`${t("admin.option")} ${oIndex + 1}`}
							className="flex-1"
						/>
						{optionFields.length > 2 && (
							<Button
								type="button"
								variant="ghost"
								size="icon"
								onClick={() => removeOption(oIndex)}
							>
								<X className="h-4 w-4" />
							</Button>
						)}
					</div>
				))}
			</div>

			<div className="space-y-2">
				<Label>{t("admin.explanation")}</Label>
				<Textarea
					{...register(`questions.${index}.explanation`)}
					rows={2}
					placeholder={t("admin.explanationPlaceholder")}
				/>
			</div>
		</div>
	);
}
